import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip component that shows content on hover
 * 
 * @param {React.ReactNode} children - The element that triggers the tooltip
 * @param {React.ReactNode} content - The content to show in the tooltip
 * @param {string} position - Preferred position: 'top', 'bottom', 'left', 'right' (default: 'top')
 * @param {number} delay - Delay in ms before showing tooltip (default: 200)
 * @param {string} className - Additional CSS classes for the wrapper
 */
function Tooltip({ children, content, position = 'top', delay = 200, className = '' }) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    let top, left;
    const gap = 8;

    switch (position) {
      case 'bottom':
        top = rect.bottom + scrollY + gap;
        left = rect.left + scrollX + (rect.width / 2);
        break;
      case 'left':
        top = rect.top + scrollY + (rect.height / 2);
        left = rect.left + scrollX - gap;
        break;
      case 'right':
        top = rect.top + scrollY + (rect.height / 2);
        left = rect.right + scrollX + gap;
        break;
      case 'top':
      default:
        top = rect.top + scrollY - gap;
        left = rect.left + scrollX + (rect.width / 2);
        break;
    }

    setCoords({ top, left });
  };

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      calculatePosition();
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Don't render tooltip if no content
  if (!content) {
    return children;
  }

  const tooltipStyles = {
    position: 'absolute',
    top: coords.top,
    left: coords.left,
    transform: position === 'top' || position === 'bottom' 
      ? 'translateX(-50%)' 
      : position === 'left' 
        ? 'translate(-100%, -50%)' 
        : 'translateY(-50%)',
    ...(position === 'top' && { transform: 'translate(-50%, -100%)' }),
    zIndex: 10000,
    padding: '8px 12px',
    backgroundColor: 'var(--surface-color)',
    color: 'var(--text-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    fontSize: '0.8125rem',
    lineHeight: '1.4',
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    pointerEvents: 'none',
    opacity: isVisible ? 1 : 0,
    transition: 'opacity 0.15s ease',
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={className}
        style={{ display: 'inline-flex' }}
      >
        {children}
      </span>
      {isVisible && createPortal(
        <div ref={tooltipRef} style={tooltipStyles}>
          {content}
        </div>,
        document.body
      )}
    </>
  );
}

export default Tooltip;
